import { AnyAction } from 'redux'
import { ActionCreator, createReducer, PayloadActionCreator, Reducer, TypeConstant } from 'typesafe-actions'
import { ProjectListQuery, ProjectStatus, TipIdKeys } from '../../responses/projects'
import { RefIntersection } from '../../responses/refs/refs'
import { DateTuple } from '../../components/data-components/date/DatePicker/DatePicker'
import { toServerDate } from '../../utils/date-utils'
import { map } from 'fp-ts/es6/Record'
import { isDefinedTuple, mergeObject, unbox } from '../../utils/utils'
import { getId } from '../../utils/entity-utils'
import { SortDirection } from '../../constants/direction'
import { TaskStatus } from '../../constants/enums'
import { TaskListQuery } from '../../responses/tasks'
import {
    DesignProductionFilters,
    sortFilters,
    taskStatusFilters
} from '../../components/pages/projects/ProjectList/ProjectFilters/filters'
import { Tag } from '../../responses/tags'
import { Bool, ID } from '../../types/UtilityTypes'

export type ProjectDropdownRefs = { [key in TipIdKeys]?: RefIntersection[] | RefIntersection | null }

const rangeToFilter = (range: DateTuple): string | [string, string] | undefined => {
    if (isDefinedTuple(range)) {
        return [toServerDate(range[0]), toServerDate(range[1])]
    }
    if (range[0]) {
        return toServerDate(range[0])
    }
}

const dropdownFilterMap = (data: RefIntersection[] | RefIntersection | null | undefined): ID[] | ID | undefined | 'null' => {
    if (Array.isArray(data)) {
        return unbox(data.map(getId))
    }
    if (data === null) {
        return 'null'
    }
    return data?.id
}

export const listFiltersToProjectQuery = (state: ProjectFiltersState, archive?: Bool): ProjectListQuery =>
    ({
        archive,
        opened_at: rangeToFilter(state.openedAt),
        search_full: state.search || undefined,
        tags: unbox(state.tags?.map(getId)),
        sort: {
            key: state.sortField?.key,
            order: state.sortDirection,
        },
        status: state.projectStatus,
        object_design_status: state.object_design_status?.key || undefined,
        object_production_status: state.object_production_status?.key || undefined,
        tasks_status: state.taskStatus.key !== TaskStatus.ALL ? state.taskStatus.key : undefined,
        ...map(dropdownFilterMap)(state.dropdowns as Required<ProjectDropdownRefs>),
    })

export const listFiltersToTaskQuery = (state: ProjectFiltersState): TaskListQuery => ({
    tasks_type: dropdownFilterMap(state.dropdowns.tasks_type),
    tasks_target_user: dropdownFilterMap(state.dropdowns.tasks_target_user),
    tasks_status: state.taskStatus.key !== TaskStatus.ALL ? state.taskStatus.key : undefined,
})

export interface ProjectFiltersState {
    dropdowns: ProjectDropdownRefs,
    openedAt: DateTuple,
    projectStatus: ProjectStatus[],
    sortField: typeof sortFilters[0] | null,
    sortDirection: SortDirection,
    taskStatus: typeof taskStatusFilters[0],
    search: string,
    tags: Tag[],
    object_design_status: typeof DesignProductionFilters[0] | null
    object_production_status: typeof DesignProductionFilters[0] | null
}


export const createProjectFilterReducer = <T extends TypeConstant>(initialState: ProjectFiltersState, actions: {
    update: PayloadActionCreator<T, Partial<ProjectFiltersState>>,
    clear: ActionCreator,
}): Reducer<ProjectFiltersState, AnyAction> => createReducer<ProjectFiltersState, AnyAction>(initialState)
    .handleAction(actions.update, (state, action) => mergeObject(action.payload)(state))
    .handleAction(actions.clear, () => initialState)